import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import type {
	ExtractInjectionAPI,
	SelectionToolbarHandler,
	FloatingToolbarSeparator,
} from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';

import {
	getAIExperienceButton,
	getImproveWritingAIButton,
	getAskAIButton,
	getDynamicFloatingToolbarButton,
} from './utils/floating-toolbar-button';

export const getSelectionToolbarConfig =
	({
		editorPluginAIProvider,
		aiGlobalOptIn,
		api,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		editorPluginAIProvider: EditorPluginAIProvider;
		api: ExtractInjectionAPI<AIPlugin> | undefined;
	}): SelectionToolbarHandler =>
	(state, intl) => {
		if (editorPluginAIProvider.disableAISelectionToolbar) {
			return;
		}
		const disabled =
			editorExperiment('platform_editor_offline_editing_web', true) &&
			api?.connectivity?.sharedState?.currentState()?.mode === 'offline';

		const isAiButtonPulse =
			api?.engagementPlatform?.sharedState.currentState()?.messageStates[
				'cc-editor-ai_ai-toolbar-button-pulse'
			];
		const handleAiButtonMount = () => {
			// Multiple calls to startMessage with the same message key will not result in multiple messages being shown.
			// That's why we can call it here without worrying about it being called multiple times.
			api?.engagementPlatform?.actions.startMessage('cc-editor-ai_ai-toolbar-button-pulse');
		};

		const improveWritingBtn = getImproveWritingAIButton({
			intl,
			hideTitle: false,
			aiGlobalOptIn,
			disabled,
		});

		const getAdditionalAIButton = () => {
			if (
				editorExperiment('editor_ai_contextual_selection_toolbar_button', 'test', {
					exposure: true,
				})
			) {
				const dynamicAIButton = getDynamicFloatingToolbarButton({
					intl,
					aiGlobalOptIn,
					state,
				});

				return dynamicAIButton ? [dynamicAIButton] : [];
			}
			return [improveWritingBtn];
		};

		if (
			// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
			fg('ai-smart-button-team-25') ||
			editorExperiment('platform_editor_controls', 'variant1')
		) {
			const askAIButton = getAskAIButton({
				intl,
				rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
				aiGlobalOptIn,
				pulse: isAiButtonPulse,
				onMount: handleAiButtonMount,
				disabled,
			});

			const separator: FloatingToolbarSeparator = {
				type: 'separator',
				fullHeight: true,
			};

			/**
			 * We want to show AI button on left most side of floating toolbar.
			 * Higher the rank, the more left it will be.
			 * We are setting rank to 10 to show AI button on left most side.
			 */
			return {
				rank: 10,
				isToolbarAbove: true,
				items: [
					...getAdditionalAIButton(),
					askAIButton,
					...(editorExperiment('platform_editor_controls', 'variant1') ? [separator] : []),
				],
			};
		} else {
			const aiExperienceBtn = getAIExperienceButton({
				intl,
				rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
				hideTitle: true,
				aiGlobalOptIn,
				title: aiMessages.tryAIToolbarIconTitle,
				tooltip: aiMessages.tryAIToolbarIconTooltip,
				pulse: isAiButtonPulse,
				onMount: handleAiButtonMount,
				disabled,
			});

			return {
				rank: -10,
				isToolbarAbove: true,
				items: [...getAdditionalAIButton(), aiExperienceBtn],
			};
		}
	};
